import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EXAMS_API } from '../config/api';
import './TakeExam.css';

/* ============================================================
   PROCTORING CONFIG  (professional / balanced)
   ============================================================ */
const MAX_WARNINGS = 5;                 // was 3 — more forgiving
const VIOLATION_DEBOUNCE_MS = 4000;     // was 1500 — swallow chained events
const BLUR_GRACE_MS = 2500;             // was 0 — ignore brief focus loss
const MOUSE_LEAVE_GRACE_MS = 3000;      // was 0 — ignore cursor leaving briefly
const RESIZE_THRESHOLD_PX = 300;        // was 150 — only large re-layouts
const VISIBILITY_MIN_HIDDEN_MS = 1500;  // only warn if tab was hidden this long

const OPTION_LABELS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const LOGIN_URL = 'https://avtechbmt.cloud/login';

/* ------------------------------------------------------------------
   Very light-weight syntax highlighter fallback (unchanged).
   ------------------------------------------------------------------ */
function highlightSnippet(code, language = '') {
  if (!code) return null;

  const KEYWORDS = {
    javascript:
      'const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|new|class|extends|import|export|from|default|async|await|try|catch|finally|throw|typeof|instanceof|this|null|undefined|true|false',
    typescript:
      'const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|new|class|extends|import|export|from|default|async|await|try|catch|finally|throw|typeof|instanceof|this|null|undefined|true|false|interface|type|enum|public|private|protected|readonly',
    python:
      'def|return|if|elif|else|for|while|break|continue|import|from|as|class|try|except|finally|raise|with|lambda|yield|global|nonlocal|pass|None|True|False|and|or|not|in|is',
    java: 'public|private|protected|class|interface|extends|implements|static|final|void|int|long|double|float|boolean|char|String|new|return|if|else|for|while|do|switch|case|break|continue|try|catch|finally|throw|throws|this|super|true|false|null|import|package',
    c: 'int|char|float|double|void|long|short|unsigned|signed|struct|union|enum|typedef|return|if|else|for|while|do|switch|case|break|continue|sizeof|static|const|extern|goto|NULL',
    cpp: 'int|char|float|double|void|long|short|unsigned|signed|struct|class|public|private|protected|return|if|else|for|while|do|switch|case|break|continue|new|delete|try|catch|throw|using|namespace|std|cout|cin|endl|true|false|nullptr',
    csharp:
      'public|private|protected|class|interface|struct|static|void|int|long|double|float|bool|char|string|var|new|return|if|else|for|foreach|while|do|switch|case|break|continue|try|catch|finally|throw|this|base|true|false|null|using|namespace',
    go: 'func|return|if|else|for|range|switch|case|break|continue|var|const|type|struct|interface|package|import|defer|go|chan|map|make|new|true|false|nil',
    rust: 'fn|let|mut|return|if|else|for|while|loop|match|struct|enum|impl|trait|pub|use|mod|true|false|None|Some|Ok|Err',
    php: 'function|return|if|else|elseif|for|foreach|while|do|switch|case|break|continue|class|extends|implements|new|public|private|protected|static|echo|print|try|catch|finally|throw|true|false|null|use|namespace',
    ruby: 'def|end|return|if|elsif|else|unless|for|while|do|case|when|break|continue|class|module|begin|rescue|ensure|raise|yield|true|false|nil|and|or|not|require|include',
    sql: 'SELECT|FROM|WHERE|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|TABLE|DROP|ALTER|JOIN|INNER|LEFT|RIGHT|OUTER|ON|GROUP|BY|ORDER|HAVING|LIMIT|OFFSET|AS|AND|OR|NOT|NULL|DISTINCT|COUNT|SUM|AVG|MIN|MAX',
  };

  const kw = KEYWORDS[language] || KEYWORDS.javascript;
  const kwRegex = new RegExp(`\\b(${kw})\\b`, 'g');

  const lines = code.split('\n');
  const nodes = [];

  lines.forEach((line, li) => {
    const pattern =
      /(\/\/[^\n]*|#[^\n]*|\/\*[\s\S]*?\*\/)|(`[^`]*`|"[^"]*"|'[^']*')|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_][A-Za-z0-9_]*\b)/g;

    let lastIndex = 0;
    let match;
    const lineParts = [];
    let key = 0;

    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        lineParts.push(
          <span key={`t-${li}-${key++}`}>
            {line.slice(lastIndex, match.index)}
          </span>
        );
      }

      const [full, comment, str, num, ident] = match;
      if (comment) {
        lineParts.push(
          <span key={`c-${li}-${key++}`} className="tok-comment">
            {comment}
          </span>
        );
      } else if (str) {
        lineParts.push(
          <span key={`s-${li}-${key++}`} className="tok-string">
            {str}
          </span>
        );
      } else if (num) {
        lineParts.push(
          <span key={`n-${li}-${key++}`} className="tok-number">
            {num}
          </span>
        );
      } else if (ident) {
        if (kwRegex.test(ident)) {
          kwRegex.lastIndex = 0;
          lineParts.push(
            <span key={`k-${li}-${key++}`} className="tok-keyword">
              {ident}
            </span>
          );
        } else if (/^[A-Z][A-Za-z0-9_]*$/.test(ident)) {
          lineParts.push(
            <span key={`cl-${li}-${key++}`} className="tok-class">
              {ident}
            </span>
          );
        } else {
          lineParts.push(<span key={`i-${li}-${key++}`}>{ident}</span>);
        }
      }

      lastIndex = pattern.lastIndex;
    }

    if (lastIndex < line.length) {
      lineParts.push(
        <span key={`r-${li}-${key++}`}>{line.slice(lastIndex)}</span>
      );
    }

    nodes.push(
      <span key={`line-${li}`}>
        {lineParts.length > 0 ? lineParts : '\u00A0'}
        {li < lines.length - 1 ? '\n' : ''}
      </span>
    );
  });

  return nodes;
}

export default function TakeExam() {
  const { id: examId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const portalRef = useRef(null);
  const submittingRef = useRef(false);
  const endTimeRef = useRef(null);
  const warningsRef = useRef(0);

  /* Debounce timer + guards */
  const lastViolationAtRef = useRef(0);

  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [visited, setVisited] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [timeLeft, setTimeLeft] = useState(null);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [warnings, setWarnings] = useState(0);

  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  /* ============================================================
     FULLSCREEN HELPERS
     ============================================================ */
  const enterFullScreen = () => {
    const elem = document.documentElement;
    try {
      if (elem.requestFullscreen) elem.requestFullscreen().catch(() => {});
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
    } catch {}
  };

  const exitFullScreen = () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    } catch {}
  };

  /* ============================================================
     INIT
     ============================================================ */
  useEffect(() => {
    initExamSession();
    // eslint-disable-next-line
  }, [examId]);

  const initExamSession = async () => {
    const authToken = token || localStorage.getItem('token');

    if (!authToken) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setScheduleInfo(null);

    const isObjectId = /^[0-9a-fA-F]{24}$/.test(examId || '');
    if (!isObjectId) {
      setError(
        `Invalid exam ID "${examId}". If you clicked an email link, use the "Accept Invitation" page instead.`
      );
      setLoading(false);
      return;
    }

    const startUrl = `${EXAMS_API}/${examId}/start`;
    const examUrl = `${EXAMS_API}/${examId}`;
    console.log('[TakeExam] Starting session:', startUrl);

    try {
      const sessionRes = await fetch(startUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (sessionRes.status === 401) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const sessionRaw = await sessionRes.text();
      let sessionData;
      try {
        sessionData = JSON.parse(sessionRaw);
      } catch {
        throw new Error(
          `Server returned non-JSON (HTTP ${sessionRes.status}). Check backend at ${EXAMS_API}.`
        );
      }

      if (sessionRes.status === 403) {
        const msg = (sessionData?.message || '').toLowerCase();

        if (msg.includes('not invited')) {
          setError(
            sessionData.message || 'You are not invited to this exam.'
          );
          setLoading(false);
          return;
        }

        if (msg.includes('opens on') || msg.includes('not yet')) {
          const opensAt =
            sessionData?.scheduledStartTime ||
            sessionData?.opensAt ||
            null;
          setScheduleInfo({
            status: 'upcoming',
            opensAt,
            message: sessionData.message,
          });
          setLoading(false);
          return;
        }

        if (msg.includes('closed') || msg.includes('window has')) {
          const closesAt =
            sessionData?.scheduledEndTime ||
            sessionData?.closesAt ||
            null;
          setScheduleInfo({
            status: 'closed',
            closesAt,
            message: sessionData.message,
          });
          setLoading(false);
          return;
        }

        setError(sessionData.message || 'Access denied.');
        setLoading(false);
        return;
      }

      if (!sessionRes.ok || !sessionData.success) {
        if (
          sessionData?.message?.toLowerCase().includes('already submitted')
        ) {
          setAlreadySubmitted(true);
          setLoading(false);
          return;
        }
        throw new Error(sessionData.message || 'Failed to start exam session.');
      }

      console.log('[TakeExam] Loading exam:', examUrl);
      const examRes = await fetch(examUrl, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const examData = await examRes.json();

      if (!examRes.ok || !examData.success) {
        throw new Error(examData.message || 'Failed to load exam details.');
      }

      setExam(examData.data);

      const endTime = new Date(sessionData.data.endTime).getTime();
      endTimeRef.current = endTime;
      calculateRemainingTime(endTime);

      enterFullScreen();
    } catch (err) {
      console.error('[TakeExam] Error:', err);
      if (err.message.includes('Failed to fetch')) {
        setError(
          `Could not reach the server at ${EXAMS_API}. Make sure the backend is running on http:// (not https://).`
        );
      } else {
        setError(err.message || 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     TIMER
     ============================================================ */
  const calculateRemainingTime = (endTimeMs) => {
    const now = Date.now();
    const diffInSeconds = Math.max(0, Math.floor((endTimeMs - now) / 1000));
    setTimeLeft(diffInSeconds);

    if (
      diffInSeconds <= 0 &&
      !submittingRef.current &&
      !result &&
      !alreadySubmitted
    ) {
      handleSubmitExam(true);
    }
  };

  useEffect(() => {
    if (timeLeft === null || result || alreadySubmitted || autoSubmitting)
      return;

    const tick = () => {
      if (!endTimeRef.current) return;
      const diff = Math.max(
        0,
        Math.floor((endTimeRef.current - Date.now()) / 1000)
      );
      setTimeLeft(diff);

      if (diff <= 0 && !submittingRef.current) {
        clearInterval(interval);
        setAutoSubmitting(true);
        handleSubmitExam(true);
      }
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [timeLeft, result, alreadySubmitted, autoSubmitting]);

  useEffect(() => {
    if (!scheduleInfo || scheduleInfo.status !== 'upcoming') return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [scheduleInfo]);

  useEffect(() => {
    if (!scheduleInfo || scheduleInfo.status !== 'upcoming') return;
    if (!scheduleInfo.opensAt) return;
    const opensAtMs = new Date(scheduleInfo.opensAt).getTime();
    if (isNaN(opensAtMs)) return;
    if (Date.now() >= opensAtMs) {
      initExamSession();
    }
    // eslint-disable-next-line
  }, [nowTick, scheduleInfo]);

  /* ============================================================
     MARK VISITED
     ============================================================ */
  useEffect(() => {
    if (exam?.questions?.[currentQuestionIndex]) {
      const qId = exam.questions[currentQuestionIndex]._id;
      setVisited((prev) => (prev[qId] ? prev : { ...prev, [qId]: true }));
    }
  }, [currentQuestionIndex, exam]);

  /* ============================================================
     ANTI-CHEAT / SECURITY — BALANCED MODE
     ============================================================ */

  /**
   * Register a violation.
   * - Debounced (VIOLATION_DEBOUNCE_MS).
   * - Severity: 'low' = informational toast (no warning count),
   *             'high' = counts toward auto-submit.
   */
  const triggerSecurityViolation = useCallback(
    (message, severity = 'high') => {
      if (submittingRef.current || result || alreadySubmitted) return;

      const now = Date.now();
      if (now - lastViolationAtRef.current < VIOLATION_DEBOUNCE_MS) return;
      lastViolationAtRef.current = now;

      // Low severity: passive informational toast, does NOT count.
      if (severity === 'low') {
        setWarningMessage(message);
        setShowWarningModal(true);
        setTimeout(() => {
          // Only dismiss if we haven't since shown a high one
          setShowWarningModal((prev) => prev);
        }, 0);
        // auto-dismiss after 3s
        const t = setTimeout(() => setShowWarningModal(false), 3000);
        return () => clearTimeout(t);
      }

      warningsRef.current += 1;
      const updated = warningsRef.current;
      setWarnings(updated);
      setWarningMessage(message);

      if (updated >= MAX_WARNINGS) {
        setShowWarningModal(false);
        setAutoSubmitting(true);
        handleSubmitExam(true);
      } else {
        setShowWarningModal(true);
      }
    },
    // eslint-disable-next-line
    [result, alreadySubmitted]
  );

  useEffect(() => {
    if (!exam || result || alreadySubmitted) return;

    const portalElem = portalRef.current;

    /* --------------------------------------------------------
       Silent blockers (no warning)
       -------------------------------------------------------- */
    const blockEvent = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
      const isDevtools =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) ||
        (e.metaKey && e.altKey && ['I', 'J', 'C'].includes(e.key));

      // Only truly dangerous shortcuts hard-blocked: view-source, save, print.
      const isBlockedShortcut =
        (e.ctrlKey || e.metaKey) &&
        ['u', 'U', 's', 'S', 'p', 'P'].includes(e.key);

      if (isDevtools || isBlockedShortcut) {
        e.preventDefault();
        if (isDevtools) {
          triggerSecurityViolation(
            'Developer-tools shortcut detected. This is not allowed.',
            'high'
          );
        }
      }
    };

    /* --------------------------------------------------------
       Trigger 1: Tab hidden — only counts if hidden long enough
       -------------------------------------------------------- */
    let hiddenAt = null;
    const handleVisibilityChange = () => {
      if (submittingRef.current || result || alreadySubmitted) return;

      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }

      if (hiddenAt) {
        const away = Date.now() - hiddenAt;
        hiddenAt = null;
        if (away >= VISIBILITY_MIN_HIDDEN_MS) {
          triggerSecurityViolation(
            'You switched away from the exam tab. Please stay on this tab.',
            'high'
          );
        }
      }
    };

    /* --------------------------------------------------------
       Trigger 2: Window blur — GRACE PERIOD
       -------------------------------------------------------- */
    let blurTimer = null;
    const handleWindowBlur = () => {
      if (submittingRef.current || result || alreadySubmitted) return;
      clearTimeout(blurTimer);
      blurTimer = setTimeout(() => {
        if (!document.hasFocus()) {
          triggerSecurityViolation(
            'Window focus was lost for too long. Please return to the exam.',
            'high'
          );
        }
      }, BLUR_GRACE_MS);
    };

    const handleWindowFocus = () => {
      clearTimeout(blurTimer);
    };

    /* --------------------------------------------------------
       Trigger 3: Mouse leaves window — GRACE PERIOD, low severity
       -------------------------------------------------------- */
    let mouseLeaveTimer = null;
    const handleMouseLeave = (e) => {
      if (submittingRef.current || result || alreadySubmitted) return;
      if (e.relatedTarget || e.toElement) return; // still inside DOM

      clearTimeout(mouseLeaveTimer);
      mouseLeaveTimer = setTimeout(() => {
        triggerSecurityViolation(
          'Your cursor left the exam window for too long.',
          'low'
        );
      }, MOUSE_LEAVE_GRACE_MS);
    };

    const handleMouseEnter = () => {
      clearTimeout(mouseLeaveTimer);
    };

    /* --------------------------------------------------------
       Trigger 4: Fullscreen exit — HIGH severity
       -------------------------------------------------------- */
    const handleFullScreenChange = () => {
      if (
        !document.fullscreenElement &&
        !submittingRef.current &&
        !result &&
        !alreadySubmitted &&
        !showWarningModal
      ) {
        triggerSecurityViolation(
          'You exited full-screen mode. Please return to full screen.',
          'high'
        );
      }
    };

    /* --------------------------------------------------------
       Trigger 5: Resize — only LARGE changes, low severity
       -------------------------------------------------------- */
    let resizeTimer = null;
    const handleResize = () => {
      if (submittingRef.current || result || alreadySubmitted) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wDiff = Math.abs(
          window.innerWidth - (window.__examWinW || window.innerWidth)
        );
        const hDiff = Math.abs(
          window.innerHeight - (window.__examWinH || window.innerHeight)
        );
        if (wDiff > RESIZE_THRESHOLD_PX || hDiff > RESIZE_THRESHOLD_PX) {
          triggerSecurityViolation(
            'The window was resized significantly. Please keep the exam maximized.',
            'low'
          );
        }
      }, 600);
    };

    /* --------------------------------------------------------
       Trigger 6: Print — block silently (no warning)
       -------------------------------------------------------- */
    const handleBeforePrint = (e) => {
      e.preventDefault?.();
    };

    /* ---- Attach listeners ---- */
    if (portalElem) {
      portalElem.addEventListener('contextmenu', blockEvent);
      portalElem.addEventListener('copy', blockEvent);
      portalElem.addEventListener('cut', blockEvent);
      portalElem.addEventListener('paste', blockEvent);
      portalElem.addEventListener('keydown', handleKeyDown);
      portalElem.addEventListener('mouseleave', handleMouseLeave);
      portalElem.addEventListener('mouseenter', handleMouseEnter);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('resize', handleResize);
    window.addEventListener('beforeprint', handleBeforePrint);

    window.__examWinW = window.innerWidth;
    window.__examWinH = window.innerHeight;

    return () => {
      if (portalElem) {
        portalElem.removeEventListener('contextmenu', blockEvent);
        portalElem.removeEventListener('copy', blockEvent);
        portalElem.removeEventListener('cut', blockEvent);
        portalElem.removeEventListener('paste', blockEvent);
        portalElem.removeEventListener('keydown', handleKeyDown);
        portalElem.removeEventListener('mouseleave', handleMouseLeave);
        portalElem.removeEventListener('mouseenter', handleMouseEnter);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('beforeprint', handleBeforePrint);
      clearTimeout(resizeTimer);
      clearTimeout(blurTimer);
      clearTimeout(mouseLeaveTimer);
    };
    // eslint-disable-next-line
  }, [exam, result, alreadySubmitted, showWarningModal, triggerSecurityViolation]);

  useEffect(() => {
    if (result || alreadySubmitted) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [result, alreadySubmitted]);

  useEffect(() => {
    if (result || alreadySubmitted) return;
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      window.history.pushState(null, '', window.location.href);
      setShowConfirmSubmit(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [result, alreadySubmitted]);

  /* ============================================================
     ANSWER HANDLERS
     ============================================================ */
  const handleOptionSelect = (questionId, optionIndex) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        questionId,
        selectedOptionIndex: optionIndex,
      },
    }));
  };

  const handleCodeChange = (questionId, code) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        questionId,
        codeAnswer: code,
      },
    }));
  };

  const toggleBookmark = (questionId) => {
    setMarkedForReview((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const clearResponse = (questionId) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  const goNext = () => {
    if (currentQuestionIndex < (exam?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  /* ============================================================
     SUBMIT
     ============================================================ */
  const handleSubmitExam = async (auto = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setShowWarningModal(false);
    setShowConfirmSubmit(false);
    setError('');
    exitFullScreen();

    const authToken = token || localStorage.getItem('token');
    const formattedAnswers = Object.values(answers);

    try {
      const res = await fetch(`${EXAMS_API}/${examId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ answers: formattedAnswers }),
      });

      const data = await res.json().catch(() => null);

      if (
        res.status === 400 &&
        data?.message?.toLowerCase().includes('already submitted')
      ) {
        setAlreadySubmitted(true);
        return;
      }

      if (res.ok && data?.success) {
        setResult({ submitted: true });
      } else {
        setError(data?.message || `Submission failed (HTTP ${res.status}).`);
      }

      if (res.status === 403) {
        const msg = (data?.message || '').toLowerCase();
        if (msg.includes('window has closed') || msg.includes('time is up')) {
          setScheduleInfo({
            status: 'closed',
            closesAt: data?.closesAt || null,
            message:
              data?.message ||
              'Your submission was not accepted because the exam window has closed.',
          });
          exitFullScreen();
          return;
        }
      }
    } catch (err) {
      setError('Network error during submission.');
    } finally {
      setSubmitting(false);
      setAutoSubmitting(false);
      if (!result) submittingRef.current = false;
    }
  };

  /* ============================================================
     HELPERS
     ============================================================ */
  const formatTime = (seconds) => {
    const s = Math.max(0, seconds || 0);
    const hrs = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    return `${String(hrs).padStart(2, '0')} : ${String(mins).padStart(
      2,
      '0'
    )} : ${String(secs).padStart(2, '0')}`;
  };

  const isLowTime = timeLeft !== null && timeLeft <= 300;

  /* ============================================================
     RENDER — LOADING
     ============================================================ */
  if (loading) {
    return (
      <div className="portal-loading">
        <h3>Initializing Secure Terminal…</h3>
      </div>
    );
  }

  /* ============================================================
     RENDER — NOT AUTHENTICATED
     ============================================================ */
  if (!isAuthenticated) {
    return (
      <div className="portal-error-screen">
        <h2>🔒 Login Required</h2>
        <p>You must be logged in to take this exam.</p>
        <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
          Please sign in or create an account to continue.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <a
            href={LOGIN_URL}
            className="btn-primary-submit"
            style={{ textDecoration: 'none' }}
          >
            Go to Login
          </a>
          <a
            href={`${LOGIN_URL}?mode=register`}
            className="btn-text-exit"
            style={{ textDecoration: 'none' }}
          >
            Create an Account
          </a>
        </div>
      </div>
    );
  }

  /* ============================================================
     RENDER — EXAM NOT OPEN YET
     ============================================================ */
  if (scheduleInfo?.status === 'upcoming') {
    const opensAtMs = scheduleInfo.opensAt
      ? new Date(scheduleInfo.opensAt).getTime()
      : null;
    const diffMs = opensAtMs ? Math.max(0, opensAtMs - nowTick) : null;
    const totalSec = diffMs !== null ? Math.floor(diffMs / 1000) : null;
    const days = totalSec !== null ? Math.floor(totalSec / 86400) : null;
    const hrs =
      totalSec !== null ? Math.floor((totalSec % 86400) / 3600) : null;
    const mins =
      totalSec !== null ? Math.floor((totalSec % 3600) / 60) : null;
    const secs = totalSec !== null ? totalSec % 60 : null;

    return (
      <div className="portal-error-screen">
        <h2>⏳ Exam Not Open Yet</h2>
        <p>
          {scheduleInfo.message ||
            'This exam is scheduled and has not started yet.'}
        </p>

        {opensAtMs && (
          <>
            <p style={{ marginTop: '0.5rem', color: '#cbd5e1' }}>
              Opens on{' '}
              <strong>{new Date(opensAtMs).toLocaleString()}</strong>
            </p>

            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                marginTop: '1.5rem',
                flexWrap: 'wrap',
              }}
            >
              <TimeBox label="Days" value={days} />
              <TimeBox label="Hours" value={hrs} />
              <TimeBox label="Minutes" value={mins} />
              <TimeBox label="Seconds" value={secs} />
            </div>

            <p
              style={{
                marginTop: '1.5rem',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}
            >
              This page will automatically refresh when the exam opens.
            </p>
          </>
        )}

        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <button
            className="btn-primary-submit"
            onClick={() => navigate('/profile')}
          >
            Back to Dashboard
          </button>
          <button className="btn-text-exit" onClick={initExamSession}>
            ↻ Refresh
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     RENDER — EXAM WINDOW CLOSED
     ============================================================ */
  if (scheduleInfo?.status === 'closed') {
    return (
      <div className="portal-error-screen">
        <h2>🔒 Exam Window Closed</h2>
        <p>
          {scheduleInfo.message ||
            'The scheduled window for this exam has ended.'}
        </p>

        {scheduleInfo.closesAt && (
          <p style={{ marginTop: '0.5rem', color: '#94a3b8' }}>
            Closed on{' '}
            <strong>{new Date(scheduleInfo.closesAt).toLocaleString()}</strong>
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <button
            className="btn-primary-submit"
            onClick={() => navigate('/profile')}
          >
            Back to Dashboard
          </button>
          <button className="btn-text-exit" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     RENDER — GENERIC ERROR
     ============================================================ */
  if (error && !exam && !result && !alreadySubmitted) {
    return (
      <div className="portal-error-screen">
        <h2>⚠️ Unable to Load Exam</h2>
        <p>{error}</p>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          API: {EXAMS_API}
        </p>
        <button
          className="btn-primary-submit"
          onClick={() => navigate('/profile')}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const totalQuestions = exam?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;
  const visitedCount = Object.keys(visited).length;
  const notVisitedCount = Math.max(0, totalQuestions - visitedCount);
  const notAnsweredCount = Math.max(0, visitedCount - answeredCount);
  const markedCount = Object.values(markedForReview).filter(Boolean).length;
  const currentQ = exam?.questions?.[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const currentHasSnippet =
    currentQ?.type === 'mcq' &&
    currentQ?.codeSnippet &&
    currentQ.codeSnippet.trim();

  /* ============================================================
     RENDER — ALREADY SUBMITTED
     ============================================================ */
  if (alreadySubmitted) {
    return (
      <div className="portal-error-screen">
        <h2>✅ Exam Already Submitted</h2>
        <p>You have already submitted this exam.</p>
        <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
          Multiple submissions are not allowed.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <button
            className="btn-primary-submit"
            onClick={() => navigate('/profile')}
          >
            Back to Dashboard
          </button>
          <button className="btn-text-exit" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-portal-wrapper" ref={portalRef}>
      {/* RESULT / SUCCESS MODAL */}
      {result && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="warning-icon">✅</div>
            <h2>Exam Submitted</h2>
            <p className="warning-subtext">
              Your answers have been submitted successfully.
              <br />
              You will be notified about your results.
            </p>
            <div className="warning-actions">
              <button
                className="btn-fullscreen"
                onClick={() => navigate('/profile')}
              >
                Back to Dashboard
              </button>
              <button className="btn-terminate" onClick={() => navigate('/')}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SECURITY WARNING MODAL */}
      {showWarningModal && !result && !alreadySubmitted && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="warning-icon">⚠️</div>
            <h2>Proctoring Warning</h2>
            <p className="warning-message">{warningMessage}</p>
            <p className="warning-subtext">
              Warning <strong>{warnings}</strong> of{' '}
              <strong>{MAX_WARNINGS}</strong>.
              <br />
              Repeated violations will auto-submit your exam. Please keep this
              tab focused and stay in full-screen mode.
            </p>
            <div className="warning-actions">
              <button
                className="btn-fullscreen"
                onClick={handleReturnToFullScreen}
              >
                Return to Full Screen
              </button>
              <button
                className="btn-terminate"
                onClick={() => setShowConfirmSubmit(true)}
              >
                Submit Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM SUBMIT MODAL */}
      {showConfirmSubmit && !result && !alreadySubmitted && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="warning-icon">📤</div>
            <h2>Submit Exam?</h2>
            <p className="warning-subtext">
              You have answered <strong>{answeredCount}</strong> of{' '}
              <strong>{totalQuestions}</strong> questions.
              <br />
              This action cannot be undone.
            </p>
            <div className="warning-actions">
              <button
                className="btn-fullscreen"
                onClick={() => setShowConfirmSubmit(false)}
              >
                Keep Working
              </button>
              <button
                className="btn-terminate"
                onClick={() => handleSubmitExam(false)}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AUTO SUBMIT SCREEN */}
      {autoSubmitting && !result && !alreadySubmitted && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="warning-icon">⏳</div>
            <h2>Auto-Submitting Exam…</h2>
            <p className="warning-subtext">
              {warnings >= MAX_WARNINGS
                ? 'Warning limit exceeded.'
                : 'Time is up.'}{' '}
              Submitting your answers now.
            </p>
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <header className="portal-header">
        <div className="portal-title-area">
          <span>{exam?.title || 'Exam'}</span>
          <span className="divider">&gt;</span>
          <span>Assessment</span>
        </div>
        <div className="portal-header-actions">
          <span
            className="warnings-counter"
            title="Each warning is a proctoring violation"
          >
            ⚠️ {warnings} / {MAX_WARNINGS}
          </span>
          <button
            className="btn-text-exit"
            onClick={() => setShowConfirmSubmit(true)}
            disabled={submitting}
          >
            Exit
          </button>
          <button
            className="btn-primary-submit"
            onClick={() => setShowConfirmSubmit(true)}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Review & Submit'}
          </button>
        </div>
      </header>

      {/* BODY */}
      <div className="portal-body">
        {/* LEFT PALETTE */}
        <aside className="portal-left-sidebar">
          <div className="palette-legend">
            <span className="legend-item legend-answered">Answered</span>
            <span className="legend-item legend-not-answered">Not Answered</span>
            <span className="legend-item legend-not-visited">Not Visited</span>
            <span className="legend-item legend-marked">Marked</span>
          </div>
          <div className="palette-grid">
            {exam?.questions?.map((q, idx) => {
              const isAnswered = Boolean(answers[q._id]);
              const isMarked = Boolean(markedForReview[q._id]);
              const isCurrent = idx === currentQuestionIndex;

              let statusClass = 'status-not-visited';
              if (isAnswered) statusClass = 'status-answered';
              else if (visited[q._id]) statusClass = 'status-not-answered';
              if (isMarked) statusClass += ' status-marked';

              return (
                <button
                  key={q._id}
                  className={`palette-box ${statusClass} ${
                    isCurrent ? 'active' : ''
                  }`}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  aria-label={`Go to question ${idx + 1}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </aside>

        {/* MIDDLE PANEL */}
        <main className="portal-main-content">
          {currentQ ? (
            <div className="question-wrapper">
              <div className="question-header">
                <span className="q-number">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </span>
                <div className="q-header-right">
                  {currentHasSnippet && (
                    <span className="q-snippet-badge">🧾 Snippet MCQ</span>
                  )}
                  <span className="q-marks">
                    🎯 {currentQ.marks || 1} mark
                    {(currentQ.marks || 1) > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="question-body-text">{currentQ.questionText}</div>

              {currentHasSnippet && (
                <div className="snippet-code-block-wrapper">
                  <div className="snippet-code-header">
                    <span className="snippet-code-lang">
                      {currentQ.language
                        ? currentQ.language.toUpperCase()
                        : 'CODE'}
                    </span>
                    <span className="snippet-code-hint">
                      Read the program and predict its output
                    </span>
                  </div>
                  <pre
                    className={`snippet-code-block ${
                      currentQ.language
                        ? `language-${currentQ.language}`
                        : ''
                    }`}
                  >
                    <code>
                      {highlightSnippet(
                        currentQ.codeSnippet,
                        currentQ.language || ''
                      )}
                    </code>
                  </pre>
                </div>
              )}

              {/* MCQ */}
              {currentQ.type === 'mcq' && (
                <div className="mcq-options-container">
                  {currentQ.options.map((opt, oIdx) => {
                    const isSelected =
                      answers[currentQ._id]?.selectedOptionIndex === oIdx;
                    return (
                      <div
                        key={oIdx}
                        className={`mcq-option-card ${
                          isSelected ? 'selected' : ''
                        }`}
                        onClick={() => handleOptionSelect(currentQ._id, oIdx)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOptionSelect(currentQ._id, oIdx);
                          }
                        }}
                      >
                        <span className="option-key">
                          {OPTION_LABELS[oIdx] || oIdx + 1})
                        </span>
                        <span className="option-text">{opt}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CODING */}
              {currentQ.type === 'coding' && (
                <div className="coding-container">
                  {currentQ.sampleInput && (
                    <div className="coding-meta">
                      <strong>Sample Input:</strong>{' '}
                      <code>{currentQ.sampleInput}</code>
                    </div>
                  )}
                  <label className="coding-label">Write Code:</label>
                  <textarea
                    key={currentQ._id}
                    rows="12"
                    value={
                      answers[currentQ._id]?.codeAnswer ??
                      currentQ.initialCodeSnippet ??
                      ''
                    }
                    onChange={(e) =>
                      handleCodeChange(currentQ._id, e.target.value)
                    }
                    className="code-editor-textarea"
                    spellCheck="false"
                  />
                </div>
              )}

              {/* BOTTOM ACTIONS */}
              <div className="question-footer-bar">
                <div className="left-action-group">
                  <button
                    className={`btn-action-bookmark ${
                      markedForReview[currentQ._id] ? 'bookmarked' : ''
                    }`}
                    onClick={() => toggleBookmark(currentQ._id)}
                  >
                    🔖{' '}
                    {markedForReview[currentQ._id] ? 'Bookmarked' : 'Bookmark'}
                  </button>

                  <button
                    className="btn-action-clear"
                    onClick={() => clearResponse(currentQ._id)}
                    disabled={!answers[currentQ._id]}
                  >
                    🔄 Clear
                  </button>
                </div>

                <div className="right-action-group">
                  <button
                    className="btn-action-clear"
                    onClick={goPrev}
                    disabled={currentQuestionIndex === 0}
                  >
                    ← Previous
                  </button>
                  <button
                    className="btn-action-save"
                    onClick={goNext}
                    disabled={isLastQuestion}
                  >
                    Save & Next →
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="question-wrapper">
              <p>No questions found for this exam.</p>
            </div>
          )}
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="portal-right-sidebar">
          <div className={`timer-card ${isLowTime ? 'timer-low' : ''}`}>
            <div className="timer-display">
              {timeLeft !== null ? formatTime(timeLeft) : '00 : 00 : 00'}
            </div>
            <div className="timer-sublabel">Hrs &nbsp; Min &nbsp; Sec</div>
            {isLowTime && (
              <div className="timer-warning">⚡ Time is running out!</div>
            )}
          </div>

          <div className="overview-section">
            <h4>Overview</h4>
            <div className="overview-row">
              <span>Total Questions</span>
              <strong>{totalQuestions}</strong>
            </div>
            <div className="overview-row">
              <span>Visited</span>
              <strong>{visitedCount}</strong>
            </div>
            <div className="overview-row">
              <span>Not Visited</span>
              <strong>{notVisitedCount}</strong>
            </div>
            <div className="overview-row">
              <span>Answered</span>
              <strong className="ok">{answeredCount}</strong>
            </div>
            <div className="overview-row">
              <span>Not Answered</span>
              <strong className="warn">{notAnsweredCount}</strong>
            </div>
            <div className="overview-row">
              <span>Marked</span>
              <strong>{markedCount}</strong>
            </div>
          </div>

          <div className="overview-section">
            <h4>Warnings</h4>
            <div className="overview-row">
              <span>Used</span>
              <strong className={warnings > 0 ? 'warn' : ''}>
                {warnings} / {MAX_WARNINGS}
              </strong>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );

  function handleReturnToFullScreen() {
    setShowWarningModal(false);
    enterFullScreen();
  }
}

/* ==========================================================================
   Small helper component for the countdown boxes
   ========================================================================== */
function TimeBox({ label, value }) {
  return (
    <div
      style={{
        minWidth: 72,
        padding: '0.75rem 0.5rem',
        borderRadius: 10,
        background: 'rgba(59, 130, 246, 0.12)',
        border: '1px solid rgba(59, 130, 246, 0.35)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: '1.6rem',
          fontWeight: 700,
          color: '#60a5fa',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value !== null ? String(value).padStart(2, '0') : '--'}
      </div>
      <div
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#94a3b8',
        }}
      >
        {label}
      </div>
    </div>
  );
}